import { s } from "../style.js";
import type { Platform } from "../types";

const smallLabel = s(
  "font-size:11px; letter-spacing:0.14em; text-transform:uppercase; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800; margin-bottom:4px"
);
const metricVal = s(
  "font-family:var(--font-heading); font-weight:800; font-size:28px; line-height:1; letter-spacing:-0.02em; font-variant-numeric:tabular-nums"
);
const cardName = s(
  "font-size:22px; font-family:var(--font-heading); font-weight:800; letter-spacing:-0.01em; margin-bottom:2px"
);

function Metric({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div>
      <div style={smallLabel}>{label}</div>
      <div style={{ ...metricVal, ...(warn ? { color: "var(--color-accent)" } : {}) }}>
        {value}
      </div>
    </div>
  );
}

function MiniBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={s("height:6px; background:var(--color-neutral-300); margin-top:6px")}>
      <div style={{ height: "100%", width: pct + "%", background: color, transition: "width .3s" }} />
    </div>
  );
}

function formatRelativeTime(isoString?: string): string {
  if (!isoString) return "—";
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

function PlatformCard({ p, pulseAnim }: { p: any; pulseAnim: string }) {
  const isDegraded = p.healthKey === "degraded";
  const isDown = p.healthKey === "down";
  const alertBorder = isDown
    ? "2px solid var(--color-accent)"
    : isDegraded
      ? "2px solid var(--color-accent)"
      : "2px solid var(--color-divider)";
  const alertBg = isDown ? "var(--color-accent-50, rgba(0,0,0,0.04))" : "var(--color-bg)";
  const deps = p.depsTotal ?? 0;
  const depsUp = p.depsUp ?? 0;
  const uptimePct = p.uptimePct != null ? `${(p.uptimePct / 100).toFixed(2)}%` : "—";
  const p95 = p.p95Latency != null ? `${p.p95Latency}ms` : "—";
  const errorRate = p.errorRate != null ? `${(p.errorRate / 100).toFixed(2)}%` : "—";
  const activeUsers = p.activeUsers != null ? p.activeUsers.toLocaleString() : "—";
  const incidentCount = p.incidents ?? 0;
  const errorPct = p.errorRate != null ? p.errorRate / 100 : 0;
  const p95Ms = p.p95Latency ?? 0;

  return (
    <div
      style={{
        ...s(
          "display:flex; flex-direction:column; min-height:0; overflow:hidden"
        ),
        borderRight: alertBorder,
        borderBottom: "2px solid var(--color-divider)",
        background: alertBg,
      }}
    >
      <div style={s("padding:20px 24px 14px; flex:none")}>
        <div style={s("display:flex; align-items:center; justify-content:space-between; margin-bottom:10px")}>
          <span style={cardName}>{p.name}</span>
          <span
            style={{
              ...s(
                "display:inline-flex; align-items:center; gap:8px; padding:5px 14px; font-size:13px; letter-spacing:0.1em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800"
              ),
              background: p.bg,
              color: p.ink,
            }}
          >
            <span
              style={{
                ...s("width:10px; height:10px; background:" + p.dot),
                animation: isDown || isDegraded ? pulseAnim : "none",
              }}
            />
            {p.label}
          </span>
        </div>
        <div
          style={s(
            "font-size:13px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; color:var(--color-neutral-600); margin-bottom:4px"
          )}
        >
          {p.region || "—"}{p.version ? ` · ${p.version}` : ""}
        </div>
      </div>

      <div style={s("flex:1; padding:0 24px 20px; display:grid; grid-template-columns:1fr 1fr; gap:14px 24px; align-content:start")}>
        <Metric label="Uptime 30d" value={uptimePct} warn={p.uptimePct != null && p.uptimePct < 9990} />
        <Metric label="P95 latency" value={p95} warn={p95Ms > 1000} />
        <Metric label="Error rate" value={errorRate} warn={errorPct > 1} />
        <Metric label="Active users" value={activeUsers} warn={p.activeUsers === 0 && isDown} />
        <div>
          <div style={smallLabel}>Dependencies</div>
          <div style={metricVal}>
            {depsUp}<span style={s("font-size:16px; color:var(--color-neutral-600)")}>/{deps}</span>
          </div>
          <MiniBar value={depsUp} max={deps} color={depsUp < deps ? "var(--color-accent)" : "var(--color-text)"} />
        </div>
        <Metric label="Open incidents" value={String(incidentCount)} warn={incidentCount > 0} />
      </div>

      <div
        style={s(
          "flex:none; display:flex; align-items:center; gap:16px; padding:10px 24px; border-top:1px solid var(--color-divider); font-size:12px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800; color:var(--color-neutral-600)"
        )}
      >
        Last checked: {formatRelativeTime(p.lastCheckAt)}
      </div>
    </div>
  );
}

export default function ServerPage({ platforms, platformsLoading, pulseAnim }: { platforms: Platform[]; platformsLoading?: boolean; pulseAnim: string }) {
  const up = platforms.filter((p: any) => p.healthKey === "up").length;
  const total = platforms.length;
  const incTotal = platforms.reduce((a: number, p: any) => a + (p.incidents ?? 0), 0);

  if (platformsLoading) {
    return (
      <div style={s("flex:1; display:flex; align-items:center; justify-content:center; color:var(--color-neutral-600)")}>
        Loading...
      </div>
    );
  }

  return (
    <div style={s("flex:1; display:flex; flex-direction:column; min-height:0; overflow:hidden")}>
      <div style={s("flex:none; display:grid; grid-template-columns:1fr 1fr 1fr; border-bottom:2px solid var(--color-divider)")}>
        <div style={s("padding:20px 32px; border-right:2px solid var(--color-divider)")}>
          <div style={s("font-size:14px; letter-spacing:0.14em; text-transform:uppercase; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800; margin-bottom:6px")}>
            Platforms operational
          </div>
          <div style={s("display:flex; align-items:baseline; gap:18px")}>
            <div style={s("font-family:var(--font-heading); font-weight:800; font-size:76px; line-height:0.9; letter-spacing:-0.04em")}>
              {up}<span style={s("font-size:36px; color:var(--color-neutral-600)")}>/{total}</span>
            </div>
            {up === total && total > 0 ? (
              <span
                style={s(
                  "display:inline-flex; align-items:center; gap:8px; padding:6px 14px; background:var(--color-text); color:var(--color-bg); font-size:14px; letter-spacing:0.1em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800"
                )}
              >
                <span style={s("width:10px; height:10px; background:var(--color-bg)")} />
                All clear
              </span>
            ) : (
              <span
                style={s(
                  "display:inline-flex; align-items:center; gap:8px; padding:6px 14px; background:var(--color-accent); color:var(--on-accent); font-size:14px; letter-spacing:0.1em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800"
                )}
              >
                <span style={{ ...s("width:10px; height:10px; background:var(--on-accent)"), animation: pulseAnim }} />
                Attention
              </span>
            )}
          </div>
        </div>
        <div style={s("padding:20px 32px; border-right:2px solid var(--color-divider)")}>
          <div style={s("font-size:14px; letter-spacing:0.14em; text-transform:uppercase; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800; margin-bottom:6px")}>
            Open incidents
          </div>
          <div style={s("font-family:var(--font-heading); font-weight:800; font-size:76px; line-height:0.9; letter-spacing:-0.04em")}>
            {incTotal}
          </div>
        </div>
        <div style={s("padding:20px 32px")}>
          <div style={s("font-size:14px; letter-spacing:0.14em; text-transform:uppercase; color:var(--color-neutral-600); font-family:var(--font-heading); font-weight:800; margin-bottom:6px")}>
            Total active users
          </div>
          <div style={s("font-family:var(--font-heading); font-weight:800; font-size:76px; line-height:0.9; letter-spacing:-0.04em; font-variant-numeric:tabular-nums")}>
            {platforms.reduce((a: number, p: any) => a + (p.activeUsers ?? 0), 0).toLocaleString()}
          </div>
        </div>
      </div>

      <div className="no-scrollbar" style={{ ...s("flex:1; min-height:0; overflow-y:auto; overflow-x:hidden; display:grid; grid-template-columns:repeat(3,1fr)"), gridAutoRows: "320px", alignContent: "start" }}>
        {platforms.map((p: any) => (
          <PlatformCard key={p.id} p={p} pulseAnim={pulseAnim} />
        ))}
      </div>
    </div>
  );
}
